import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import type { Report } from 'powerbi-client';

import { ENABLE_ASK_SYNTIRO_CHAT } from '../env';
import type {
  AskContextItem,
  AskReport,
  AskToolCall
} from '../features/ask-syntiro/types/syntiro-agent';
import { fulfilVisualsDataRequest } from '../features/ask-syntiro/tools/report/visual-data';
import {
  buildAskReportFilters,
  buildCapturedAskReport
} from '../features/ask-syntiro/utils/ask-report.util';
import type { VisualIdsByHandle } from '../features/ask-syntiro/utils/ask-report.util';
import {
  captureReportFilters,
  captureReportVisuals,
  logReportCaptureFailure
} from '../lib/app/powerbi/report-visual-capture';
import { UserContext } from './UserContext';
import { GroupContext } from './GroupContext';
import { CompanyContext } from './CompanyContext';

export type AskContextResolver = () => Promise<AskContextItem | null> | AskContextItem | null;

/** The live report Ask Syntiro can question, and what the last capture learned about it. The
 * manifest is captured once per question; `visualIdsByHandle` is what lets a tool call re-export
 * the handles it asks for from `report` rather than from rows held since the manifest was built. */
interface CurrentAskReport {
  reportId: string;
  report: Report;
  manifest: AskReport | null;
  visualIdsByHandle: VisualIdsByHandle | null;
}

interface AskSyntiroContextProps {
  askSyntiroEnabled: boolean;
  askSyntiroOpen: boolean;
  openAskSyntiro: () => void;
  closeAskSyntiro: () => void;
  registerAskContextResolver: (kind: string, resolver: AskContextResolver) => () => void;
  resolveAskContext: () => Promise<Array<AskContextItem>>;
  registerAskReport: (reportId: string, report: Report) => () => void;
  captureAskReport: () => Promise<AskReport | undefined>;
  fulfilReportDataRequest: (toolCall: AskToolCall) => Promise<AskReport | undefined>;
}

export const AskSyntiroContext = createContext<AskSyntiroContextProps>({
  askSyntiroEnabled: false,
  askSyntiroOpen: false,
  openAskSyntiro: () => null,
  closeAskSyntiro: () => null,
  registerAskContextResolver: () => () => null,
  resolveAskContext: () => Promise.resolve([]),
  registerAskReport: () => () => null,
  captureAskReport: () => Promise.resolve(undefined),
  fulfilReportDataRequest: () => Promise.resolve(undefined)
});

const AskSyntiroContextProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useContext(UserContext);
  const { group } = useContext(GroupContext);
  const { company } = useContext(CompanyContext);

  const [askSyntiroOpen, setAskSyntiroOpen] = useState(false);

  const contextResolversRef = useRef(new Map<string, AskContextResolver>());
  const currentAskReportRef = useRef<CurrentAskReport | null>(null);

  const askSyntiroEnabled = ENABLE_ASK_SYNTIRO_CHAT && user?.super_admin === true;

  const openAskSyntiro = useCallback(() => {
    setAskSyntiroOpen(true);
  }, []);

  const closeAskSyntiro = useCallback(() => {
    setAskSyntiroOpen(false);
  }, []);

  const registerAskContextResolver = useCallback(
    (kind: string, resolver: AskContextResolver): (() => void) => {
      contextResolversRef.current.set(kind, resolver);
      return () => {
        if (contextResolversRef.current.get(kind) === resolver) {
          contextResolversRef.current.delete(kind);
        }
      };
    },
    []
  );

  const resolveAskContext = useCallback(async (): Promise<Array<AskContextItem>> => {
    const resolverEntries = Array.from(contextResolversRef.current.values());
    const settledResults = await Promise.allSettled(
      resolverEntries.map(async (resolver) => resolver())
    );

    const contextItems: Array<AskContextItem> = [];
    settledResults.forEach((result) => {
      if (result.status === 'fulfilled' && result.value !== null) {
        contextItems.push(result.value);
      }
    });
    return contextItems;
  }, []);

  /** Registers the live report as the one Ask Syntiro may question. Returns an identity-checked
   * unregister, so a hidden report's cleanup cannot displace a newer one. */
  const registerAskReport = useCallback((reportId: string, report: Report): (() => void) => {
    currentAskReportRef.current = { reportId, report, manifest: null, visualIdsByHandle: null };

    return () => {
      if (currentAskReportRef.current?.reportId === reportId) {
        currentAskReportRef.current = null;
      }
    };
  }, []);

  /** Captures the manifest and filter state for one question. */
  const captureAskReport = useCallback(async (): Promise<AskReport | undefined> => {
    const currentAskReport = currentAskReportRef.current;
    if (!currentAskReport) return undefined;

    const reportCaptureResult = await captureReportVisuals(currentAskReport.report).catch(
      (err: unknown) => {
        logReportCaptureFailure('captureAskReport', err, { reportId: currentAskReport.reportId });
        return null;
      }
    );
    if (!reportCaptureResult) return undefined;

    const capturedAskReport = buildCapturedAskReport(reportCaptureResult, Date.now());
    if (!capturedAskReport) return undefined;

    // Dropped if the report changed mid-capture: the manifest describes one that is no longer
    // registered, and the handles in it would resolve against the wrong report.
    if (currentAskReportRef.current?.reportId !== currentAskReport.reportId) return undefined;

    currentAskReportRef.current = {
      ...currentAskReport,
      manifest: capturedAskReport.report,
      visualIdsByHandle: capturedAskReport.visualIdsByHandle
    };

    return capturedAskReport.report;
  }, []);

  /** Answers a tool call: the requested visuals are re-exported from the live report and the filter
   * state re-read, so the rows and the filters describing them share one moment. The manifest is
   * carried over from this question's capture — its handles are what the model asked by. */
  const fulfilReportDataRequest = useCallback(
    async (toolCall: AskToolCall): Promise<AskReport | undefined> => {
      const currentAskReport = currentAskReportRef.current;
      if (!currentAskReport?.manifest) return undefined;

      const [reportData, filterCaptureResult] = await Promise.all([
        fulfilVisualsDataRequest({
          toolCall,
          report: currentAskReport.report,
          visualIdsByHandle: currentAskReport.visualIdsByHandle
        }),
        captureReportFilters(currentAskReport.report).catch((err: unknown) => {
          logReportCaptureFailure('fulfilReportDataRequest', err, {
            reportId: currentAskReport.reportId
          });
          return null;
        })
      ]);

      return {
        reportManifest: currentAskReport.manifest.reportManifest,
        reportFilters: filterCaptureResult
          ? buildAskReportFilters(filterCaptureResult)
          : currentAskReport.manifest.reportFilters,
        reportData
      };
    },
    []
  );

  useEffect(() => {
    setAskSyntiroOpen(false);
    currentAskReportRef.current = null;
  }, [company?.id, group?.id, user?.id]);

  const value = {
    askSyntiroEnabled,
    askSyntiroOpen,
    openAskSyntiro,
    closeAskSyntiro,
    registerAskContextResolver,
    resolveAskContext,
    registerAskReport,
    captureAskReport,
    fulfilReportDataRequest
  };

  return <AskSyntiroContext.Provider value={value}>{children}</AskSyntiroContext.Provider>;
};

export default AskSyntiroContextProvider;
